import type { AxisDomain } from 'recharts/types/util/types';

import { CSAT_TICKS, formatCsatTick } from '../../utils/chartConfig';
import { formatDurationFromSeconds } from '../../utils/formatDuration';
import type { FillPolicy } from '../../utils/padSeriesToPeriod';

export type HumanAgentsMetricKey = 'userCsat' | 'aiCsat' | 'totalConversations' | 'resolutionTime';

export interface HumanAgentsMetricConfig {
  labelKey: string;
  domain: AxisDomain;
  valueFormatter: (value: number) => string;
  tooltipValueFormatter: (value: number) => string;
  ticks?: number[];
  tickFormatter?: (value: number) => string;
  /**
   * How empty days are filled when padding to the selected period: `zero` for
   * counts (line dips to 0), `gap` for averages (line breaks, no fabricated
   * value).
   */
  fill: FillPolicy;
}

const CSAT_CONFIG: Omit<HumanAgentsMetricConfig, 'labelKey'> = {
  domain: [0, 5],
  valueFormatter: (value) => value.toFixed(1),
  tooltipValueFormatter: (value) => `${value.toFixed(1)}/5`,
  ticks: CSAT_TICKS,
  tickFormatter: formatCsatTick,
  fill: 'gap',
};

export const HUMAN_AGENTS_METRIC_CONFIGS: Record<HumanAgentsMetricKey, HumanAgentsMetricConfig> = {
  userCsat: { labelKey: 'analytics.human_agents_overview.tooltip_user_csat_score', ...CSAT_CONFIG },
  aiCsat: { labelKey: 'analytics.human_agents_overview.tooltip_ai_csat_score', ...CSAT_CONFIG },
  totalConversations: {
    labelKey: 'analytics.human_agents_overview.tooltip_conversations',
    domain: [0, 'auto'],
    valueFormatter: (value) => value.toLocaleString(),
    tooltipValueFormatter: (value) => value.toLocaleString(),
    fill: 'zero',
  },
  resolutionTime: {
    labelKey: 'analytics.human_agents_overview.avg_resolution_time',
    domain: [0, 'auto'],
    valueFormatter: formatDurationFromSeconds,
    tooltipValueFormatter: formatDurationFromSeconds,
    fill: 'gap',
  },
};

export interface HumanAgentsTimeseriesPoint {
  date: string;
  userCsat: number | null;
  userReviews: number;
  aiCsat: number | null;
  aiReviews: number;
  conversations: number;
  resolutionTimeSeconds: number;
}
